import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import { FileIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import sanitizeFilename from '@orn/shared-web/utils/sanitizeFilename';
import * as React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import type { IConversationMessage } from '../../hooks/useAssistantWebSocket';
import type { IObservationDocument } from '@orn/global/assistant/snapshotMessage.js';

interface IAssistantMessageProps {
  message: IConversationMessage;
}

const ATTACHED_DOC_FALLBACK = 'Attached document';

/**
 * Renders a single message with appropriate styling for user vs assistant.
 * User turns may carry an `attachments` array — rendered as a chip row
 * above the content bubble, filename + file icon per chip. v1 stops at
 * filename / icon; thumbnails + lightbox are post-MVP. Server-supplied
 * filenames are truncated and stripped of control chars before render so
 * a 500-char or escape-laden filename can't break layout.
 */
const AssistantMessage = ({ message }: IAssistantMessageProps) => {
  const isUser = message.role === 'user';
  // Mid-turn-typed user bubble waiting for the previous turn to finish — render
  // a lighter blue so it reads as "queued" vs the fully-saturated "in flight"
  // bubble above the thinking box. Assistant bubbles never carry these flags.
  const isQueued = isUser && message.isPending === true && message.isInFlight === false;

  return (
    <div className={cx('flex w-full flex-col', isUser ? 'items-end' : 'items-start')}>
      {isUser && message.attachments && message.attachments.length > 0 && (
        <UserAttachmentsRow attachments={message.attachments} />
      )}
      <div
        className={cx(
          'max-w-[85%] rounded-2xl px-4 py-3',
          isUser ? cx('text-white', isQueued ? 'bg-brand-solid/70' : 'bg-brand-solid') : 'bg-secondary text-primary'
        )}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">{message.content}</div>
        ) : (
          <div className="text-primary text-sm leading-relaxed">
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                h2: ({ children }) => (
                  <div className="text-primary mb-2 mt-3 text-base font-semibold first:mt-0">{children}</div>
                ),
                h3: ({ children }) => (
                  <div className="text-primary mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</div>
                ),
                p: ({ children }) => <div className="mb-2 last:mb-0">{children}</div>,
                strong: ({ children }) => <span className="font-semibold">{children}</span>,
                ul: ({ children }) => <ul className="mb-2 list-outside list-disc pl-5">{children}</ul>,
                ol: ({ children }) => <ol className="mb-2 list-outside list-decimal pl-5">{children}</ol>,
                li: ({ children }) => <li className="mb-0.5 pl-1">{children}</li>,
                hr: () => <hr className="border-primary my-3" />,
                table: ({ children }) => (
                  <div className="my-2 overflow-x-auto">
                    <table className="border-primary w-full border-collapse border text-xs">{children}</table>
                  </div>
                ),
                thead: ({ children }) => <thead className="bg-tertiary">{children}</thead>,
                tbody: ({ children }) => <tbody>{children}</tbody>,
                tr: ({ children }) => <tr className="border-primary border-b last:border-b-0">{children}</tr>,
                th: ({ children }) => (
                  <th className="border-primary text-primary border-r px-2 py-1 text-left font-semibold last:border-r-0">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="border-primary text-primary border-r px-2 py-1 align-top last:border-r-0">
                    {children}
                  </td>
                ),
                code: ({ children }) => (
                  <code className="bg-tertiary rounded px-1 py-0.5 font-mono text-xs">{children}</code>
                ),
              }}
            >
              {message.content}
            </Markdown>
            {/* While this round's text streams, the "working" affordance sits on
                its own line at the bottom of the growing bubble. On finalize the
                flag clears and the spinner pops back out to its own bubble (the
                pane's standalone spinner) until the turn ends. */}
            {message.isStreaming === true && (
              // `w-fit` shrinks the indicator's own centering box to the spinner
              // so it sits at the left edge of the (text-width) bubble, not
              // centered across it.
              <LoadingIndicator type="line-spinner" size="xs" className="mt-2 w-fit" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface IUserAttachmentsRowProps {
  attachments: IObservationDocument[];
}

const UserAttachmentsRow = ({ attachments }: IUserAttachmentsRowProps) => (
  <div className="mb-1 flex max-w-[85%] flex-wrap justify-end gap-1.5">
    {attachments.map((doc) => (
      <div
        key={doc.id}
        className="bg-secondary border-primary text-primary flex max-w-xs items-center gap-1.5 rounded-lg border px-2 py-1 text-xs"
        title={doc.name ?? ATTACHED_DOC_FALLBACK}
      >
        <FileIcon className="text-tertiary size-3.5 shrink-0" />
        <span className="truncate">{sanitizeFilename(doc.name)}</span>
      </div>
    ))}
  </div>
);

// Memoized: token-level streaming re-creates the messages array on every delta,
// but only the growing bubble's `message` object changes identity. Shallow-prop
// memo lets every untouched bubble skip its (markdown) re-render.
export default React.memo(AssistantMessage);
